<script lang="ts">
  // The headline measurements of an activity: a thin bar putting each average in context of the
  // ride's own maximum, with the numbers under it.
  //
  // Three hand-built arrangements rather than one that reflows:
  //   row  -- a single divided row, one column per measurement
  //   grid -- a fixed two-up grid, so nothing wraps and nothing scrolls
  //   rows -- one labelled row per measurement, read as a table down a phone screen (the xs tier)
  //
  // A measurement's numbers are never allowed to ellipsis away -- "2 044" and "1 730 kJ" are the
  // whole point of the strip. Two things keep that true: columns are widened in proportion to how
  // many numbers they carry (XSS has four, Cadence has two), and the single row is only used when
  // the strip has actually measured enough width for every column's content. Otherwise it drops to
  // the two-up grid, where each measurement gets half the strip.
  //
  // XSS is the odd one out: its bar is the low/high/peak buckets stacked to make up the total,
  // because for XSS the split *is* the story.
  import { cn } from '@/lib/utils';
  import { kmToMile, useImperialUnits } from '@/Util/utils';
  import LinearGauge from './LinearGauge.svelte';
  import type { CardLayout, DashboardActivity } from './activityCardUtils';

  export let exercise: DashboardActivity;
  export let layout: CardLayout = 'lg';

  const imperial = useImperialUnits();

  const XLSS = 'var(--color-xlss)';
  const XHSS = 'var(--color-xhss)';
  const XPSS = 'var(--color-xpss)';

  const round = (v: number, dp = 0) => {
    const f = Math.pow(10, dp);
    return Math.round(v * f) / f;
  };
  /** Thousands separators on the big numbers ("2,204 kJ"), none on the small ones. */
  const fmt = (v: number, dp = 0) => round(v, dp).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
  /** Treat 0 as "not measured" -- these all come through as 0.0 when the device didn't record them. */
  const num = (v: unknown): number | null => (typeof v === 'number' && isFinite(v) && v > 0 ? v : null);

  type GaugeStat = { value: string; unit?: string; label: string; dot?: string };
  type Gauge = {
    key: string;
    title: string;
    color: string;
    tone: 'colored' | 'muted';
    scale: 'linear' | 'log';
    avg: number | null;
    max: number | null;
    /** Set instead of avg/max to draw a stacked bar (XSS buckets). */
    segments?: { value: number; color: string }[];
    stats: GaugeStat[];
  };

  $: speed = (v: number) => (imperial ? kmToMile(v) : v);
  $: speedUnit = imperial ? 'mph' : 'km/h';

  let gauges: Gauge[] = [];
  $: gauges = [buildXss(exercise), buildPower(exercise), buildSpeed(exercise), buildCadence(exercise), buildHeartRate(exercise)]
    .filter((gauge): gauge is Gauge => gauge != null);

  function buildXss(a: DashboardActivity): Gauge | null {
    const xss = num(a?.xss);
    if (!xss) return null;
    const low = a?.xlss ?? 0;
    const high = a?.xhss ?? 0;
    const peak = a?.xpss ?? 0;
    return {
      key: 'xss',
      title: 'XSS',
      color: XLSS,
      tone: 'colored',
      scale: 'linear',
      avg: null,
      max: null,
      segments: [
        { value: low, color: XLSS },
        { value: high, color: XHSS },
        { value: peak, color: XPSS },
      ],
      stats: [
        { value: fmt(xss), label: 'Total' },
        { value: fmt(low), label: 'Low', dot: XLSS },
        { value: fmt(high, 1), label: 'High', dot: XHSS },
        { value: fmt(peak, 1), label: 'Peak', dot: XPSS },
      ],
    };
  }

  function buildPower(a: DashboardActivity): Gauge | null {
    // avgPower is the server's already-rounded figure; avg_power is the raw accessor behind it.
    const avg = num(a?.avgPower) ?? num(a?.avg_power);
    const max = num(a?.maxPower) ?? num(a?.max_power);
    const work = num(a?.tw);
    if (!avg && !max) return null;
    return {
      key: 'power',
      title: 'Power',
      // Neutral grey rather than a colour of its own: a handful of sprint spikes would otherwise
      // make the power gauge the loudest thing on the card.
      color: 'hsl(var(--muted-foreground))',
      tone: 'muted',
      // Log scale so the difference between 180 W and 260 W stays visible next to a 1200 W spike.
      scale: 'linear',
      avg,
      max,
      stats: [
        ...(avg ? [{ value: fmt(avg), unit: 'W', label: 'Avg' }] : []),
        ...(max ? [{ value: fmt(max), unit: 'W', label: 'Max' }] : []),
        ...(work ? [{ value: fmt(work / 1000), unit: 'kJ', label: 'Work' }] : []),
      ],
    };
  }

  function buildSpeed(a: DashboardActivity): Gauge | null {
    const avg = num(a?.avg_speed);
    const max = num(a?.max_speed);
    if (!avg && !max) return null;
    return {
      key: 'speed',
      title: 'Speed',
      color: 'var(--color-xert-lime)',
      tone: 'colored',
      scale: 'linear',
      avg: avg != null ? speed(avg) : null,
      max: max != null ? speed(max) : null,
      stats: [
        ...(avg ? [{ value: fmt(speed(avg), 1), unit: speedUnit, label: 'Avg' }] : []),
        ...(max ? [{ value: fmt(speed(max), 1), unit: speedUnit, label: 'Max' }] : []),
      ],
    };
  }

  function buildCadence(a: DashboardActivity): Gauge | null {
    const avg = num(a?.avg_cadence);
    const max = num(a?.max_cadence);
    if (!avg && !max) return null;
    return {
      key: 'cadence',
      title: 'Cadence',
      color: 'var(--color-xert-teal)',
      tone: 'colored',
      scale: 'linear',
      avg,
      max,
      stats: [
        ...(avg ? [{ value: fmt(avg), unit: 'rpm', label: 'Avg' }] : []),
        ...(max ? [{ value: fmt(max), unit: 'rpm', label: 'Max' }] : []),
      ],
    };
  }

  function buildHeartRate(a: DashboardActivity): Gauge | null {
    const avg = num(a?.avgHeartRate) ?? num(a?.avg_heart_rate);
    const max = num(a?.maxHeartRate) ?? num(a?.max_heart_rate);
    if (!avg && !max) return null;
    return {
      key: 'hr',
      title: 'Heart Rate',
      color: XPSS,
      tone: 'colored',
      scale: 'linear',
      avg,
      max,
      stats: [
        ...(avg ? [{ value: fmt(avg), unit: 'bpm', label: 'Avg' }] : []),
        ...(max ? [{ value: fmt(max), unit: 'bpm', label: 'Max' }] : []),
      ],
    };
  }

  // Approximate advance widths, taken from the classes these actually render with. Estimating from
  // the strings rather than assuming one width for every number matters a lot here: "243" needs
  // about 40px and "53,2 km/h" about 73px, and a single flat figure for both either sends the row
  // to the grid far earlier than it needs to or lets it crowd.
  const VALUE_CHAR = 8.2; // a digit at text-sm semibold
  const UNIT_CHAR = 5.4; // text-[11px] normal
  const UNIT_GAP = 3; // ml-0.5 before the unit
  const DOT_WIDTH = 10; // the XSS bucket dot and its margin
  const STAT_GAP = 16; // gap-x-4 between one number and the next
  /** Each column's own left/right padding plus its divider. */
  const COLUMN_PADDING = 24;

  /**
   * What one number needs. Only the value is accounted for -- the label under it is smaller than
   * any value it sits below, and is the one thing here that's allowed to truncate.
   */
  function statWidth(stat: GaugeStat): number {
    return (
      (stat.dot ? DOT_WIDTH : 0) +
      stat.value.length * VALUE_CHAR +
      (stat.unit ? UNIT_GAP + stat.unit.length * UNIT_CHAR : 0) +
      STAT_GAP
    );
  }

  let stripSize: ResizeObserverSize[] | null = null;
  let stripWidth = 0;
  $: if (stripSize && stripSize[0]) stripWidth = stripSize[0].inlineSize;

  /** What each column needs for its own numbers, which is also how the row shares out its width. */
  $: gaugeWidths = gauges.map((gauge) => gauge.stats.reduce((total, stat) => total + statWidth(stat), 0));
  $: requiredRowWidth = gaugeWidths.reduce((total, width) => total + width, 0) + gauges.length * COLUMN_PADDING;
  // Measured rather than keyed off the card's layout tier: how much room the row needs depends on
  // which measurements this activity actually recorded, and a ride with heart rate needs a column
  // more than one without. Switching between row and grid changes the strip's height, never its
  // width, so this can't feed back on itself.
  // Before the observer has reported, take the card's own tier as the guess -- it defaults to lg
  // for the same reason, and guessing right avoids a visible reflow on the common desktop case.
  $: fittedMode = stripWidth > 0 ? (stripWidth >= requiredRowWidth ? 'row' : 'grid') : layout === 'lg' ? 'row' : 'grid';
  $: mode = layout === 'xs' ? 'rows' : fittedMode;

  /** Percentage widths for the stacked XSS bar; a bucket that rounds to nothing is dropped. */
  function segmentWidths(segments: { value: number; color: string }[]): { pct: number; color: string }[] {
    const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0);
    if (total <= 0) return [];
    return segments
      .map((segment) => ({ pct: (Math.max(0, segment.value) / total) * 100, color: segment.color }))
      .filter((segment) => segment.pct > 0.5);
  }
</script>

{#if gauges.length}
  <div
    bind:borderBoxSize={stripSize}
    class={cn(
      mode === 'rows'
        ? 'flex flex-col divide-y divide-x-0 divide-solid divide-border'
        : mode === 'row'
          ? 'flex flex-row items-start divide-x divide-y-0 divide-solid divide-border'
          : 'grid grid-cols-2 gap-x-5 gap-y-3'
    )}
  >
    {#each gauges as gauge, index (gauge.key)}
      <!-- In row mode the flex basis is the column's own padding and the grow factor is what its
           numbers actually need, so the space left over after padding is divided in exactly the
           proportion requiredRowWidth was measured in: if the row fits at all, every column
           provably clears its own minimum rather than just the total clearing. -->
      <div
        class={cn(
          'min-w-0',
          mode === 'rows' ? 'flex flex-row items-center gap-3 py-2 first:pt-0 last:pb-0' : '',
          mode === 'row' ? cn(index === 0 ? 'pr-4' : 'px-4', index === gauges.length - 1 ? 'pr-0' : '') : ''
        )}
        style={mode === 'row' ? `flex: ${gaugeWidths[index]} 1 ${COLUMN_PADDING}px` : ''}
      >
        <!-- On a phone the title becomes the row's label column, so the rows line up as a table. -->
        <div
          class={cn(
            'text-[10px] uppercase tracking-wider text-muted-foreground leading-tight',
            mode === 'rows' ? 'w-[62px] shrink-0' : ''
          )}
        >
          {gauge.title}
        </div>

        <div class={cn('min-w-0', mode === 'rows' ? 'flex-1' : '')}>
          <div class="mt-1.5">
            {#if gauge.segments}
              <div class="flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
                {#each segmentWidths(gauge.segments) as segment}
                  <div style="width: {segment.pct}%; background-color: {segment.color}" />
                {/each}
              </div>
            {:else}
              <LinearGauge avg={gauge.avg} max={gauge.max} color={gauge.color} scale={gauge.scale} tone={gauge.tone} />
            {/if}
          </div>

          <!-- Content-sized columns, not equal ones: an equal split gives "2 044" the same room as
               "3,4" and ellipses the number that mattered. The label may still truncate; the number
               never does. One row, so "Peak" can't drop onto a line of its own. -->
          <div class="mt-1.5 grid justify-start gap-x-4" style="grid-auto-flow: column; grid-auto-columns: max-content;">
            {#each gauge.stats as stat}
              <div class="min-w-0">
                <div class="text-sm font-semibold leading-tight whitespace-nowrap">
                  {#if stat.dot}<span class="inline-block h-1.5 w-1.5 rounded-full align-middle mr-1" style="background-color: {stat.dot}" />{/if}{stat.value}{#if stat.unit}<span class="font-normal text-muted-foreground text-[11px] ml-0.5">{stat.unit}</span>{/if}
                </div>
                <div class="text-[10px] text-muted-foreground leading-none truncate">{stat.label}</div>
              </div>
            {/each}
          </div>
        </div>
      </div>
    {/each}
  </div>
{/if}
